// Epoch 17: Lead Responses Inbox System - TypeScript Types
// Date: July 25, 2025

import { Campaign, User } from './index';

export type LeadResponseStatus = 'new' | 'read' | 'reply_requested' | 'reply_sent' | 'closed';

export interface ConversationMessage {
  id: string;
  sender: 'user' | 'lead';
  content: string;
  timestamp?: string;
  sender_name?: string;
  confidence?: number;
  detectionMethod?: string;
}

export interface OutreachTeamMember {
  id: string;
  team_member_name: string;
  is_active: boolean;
  role_description?: string;
  contact_email?: string;
  team_lead_id?: string;
  created_at: string;
  updated_at: string;
  aliases: OutreachTeamAlias[];
}

export interface OutreachTeamAlias {
  id: string;
  team_config_id: string;
  alias_name: string;
  is_active: boolean;
  created_at: string;
}

export interface ParsingCorrection {
  message_id: string;
  field: string;
  old_value: unknown;
  new_value: unknown;
  timestamp: string;
  corrected_by: string;
}

export type PipelineStage =
  | 'Lead Responded'
  | '1st Mtg Scheduled'
  | '1st Mtg Taken'
  | 'Evaluating'
  | 'Proposal Made'
  | 'Negotiating'
  | 'Nurturing'
  | 'Unqualified'
  | 'Closed Won'
  | 'Closed Lost';

export const PIPELINE_STAGES: PipelineStage[] = [
  'Lead Responded',
  '1st Mtg Scheduled',
  '1st Mtg Taken',
  'Evaluating',
  'Proposal Made',
  'Negotiating',
  'Nurturing',
  'Unqualified',
  'Closed Won',
  'Closed Lost',
];

export const PIPELINE_STAGE_COLORS: Record<PipelineStage, string> = {
  'Lead Responded':    'bg-gray-100 text-gray-700',
  '1st Mtg Scheduled':'bg-blue-100 text-blue-800',
  '1st Mtg Taken':    'bg-blue-200 text-blue-900',
  'Evaluating':       'bg-amber-100 text-amber-800',
  'Proposal Made':    'bg-orange-100 text-orange-800',
  'Negotiating':      'bg-purple-100 text-purple-800',
  'Nurturing':        'bg-teal-100 text-teal-800',
  'Unqualified':      'bg-red-100 text-red-700',
  'Closed Won':       'bg-green-100 text-green-800',
  'Closed Lost':      'bg-red-200 text-red-900',
};

export interface LeadResponse {
  id: string;
  campaign_id: string;
  company_id: string;
  assigned_csr: string | null;
  
  // Lead Information
  lead_name: string;
  lead_linkedin_url: string;
  lead_company: string;
  lead_title?: string;
  lead_location?: string;
  alias_profile: string;
  
  // Post-Epoch 22: Additional Contact Fields
  lead_email?: string;
  lead_phone?: string;
  lead_website?: string;
  
  // Conversation Data
  conversation_raw: string;
  conversation_parsed?: ConversationMessage[];
  
  // Status Management
  status: LeadResponseStatus;
  read_by_user_at?: string;
  
  // Notes
  csr_notes: string;
  
  // Timestamps
  created_at: string;
  updated_at: string;
  
  // EPOCH 29: Threading fields
  parent_response_id?: string;
  conversation_version?: number;
  message_count?: number;
  is_latest_version?: boolean;
  latest_message_at?: string;
  
  // EPOCH 70: Analytics tracking fields
  sequence_message_number?: number;
  response_trigger?: 'drip_sequence' | 'reply_request' | 'sequence_continuation';
  pipeline_stage?: PipelineStage;
  pipeline_stage_updated_by?: string;
  pipeline_stage_updated_at?: string;
  is_first_response?: boolean;

  // EPOCH 28: AI Scoring fields
  ai_sentiment_score?: number;
  ai_interest_level?: number;
  ai_interest_category?: string;
  ai_buying_signals?: string[];
  ai_objections?: string[];
  ai_next_action?: string;
  ai_scoring_confidence?: number;
  ai_scoring_timestamp?: string;
  ai_scoring_model?: string;
  
  // Relations (populated via joins)
  campaign?: Campaign;
  assigned_csr_user?: User;
  reply_requests?: ReplyRequest[];
}

export interface ReplyRequest {
  id: string;
  lead_response_id: string;
  user_id: string;

  // Request Details
  reply_content: string;
  user_notes?: string;
  template_used?: string;
  is_sequence_continuation?: boolean;

  // CSR Response
  actual_message_sent?: string;
  csr_notes?: string;
  sent_at?: string;
  marked_sent_by?: string;

  // Timestamps
  created_at: string;
  updated_at: string;

  // Relations (populated via joins)
  user?: User;
  marked_sent_by_user?: User;
  lead_response?: LeadResponse;
}

export interface ReplyTemplate {
  id: string;
  user_id: string;
  template_name: string;
  template_content: string;
  usage_count: number;
  created_at: string;
  updated_at: string;
}

// Form interfaces for API requests
export interface CSRLeadResponseForm {
  campaign_id: string;
  lead_name: string;
  lead_linkedin_url: string;
  lead_company: string;
  lead_title: string;
  lead_location: string;
  alias_profile: string;
  conversation_raw: string;
  csr_notes: string;
  
  // Post-Epoch 22: Additional Contact Fields
  lead_email?: string;
  lead_phone?: string;
  lead_website?: string;
  
  // AI-parsed conversation from validation interface
  conversation_parsed?: ConversationMessage[];

  // EPOCH 70: Analytics tracking fields
  sequence_message_number?: number;
  response_trigger?: string;
}

export interface UserReplyRequestForm {
  lead_response_id: string;
  reply_content: string;
  user_notes: string;
  template_used: string;
  is_sequence_continuation?: boolean;
}

export interface CSRReplySentForm {
  actual_message_sent: string;
  csr_notes: string;
  sent_at: string;
}

export interface ReplyTemplateForm {
  template_name: string;
  template_content: string;
}

// API Response interfaces
export interface LeadResponsesListResponse {
  data: LeadResponse[];
  count: number;
  page?: number;
  limit?: number;
}

export interface LeadResponseDetailResponse {
  data: LeadResponse;
}

export interface ReplyRequestsListResponse {
  data: ReplyRequest[];
  count: number;
}

export interface ReplyTemplatesListResponse {
  data: ReplyTemplate[];
  count: number;
}

// Filter and search interfaces
export interface LeadResponseFilters {
  status?: LeadResponseStatus | 'all';
  campaign?: string | 'all';
  search?: string;
  assigned_csr?: string;
  date_from?: string;
  date_to?: string;
}

export interface LeadResponseSortOptions {
  field: 'created_at' | 'updated_at' | 'lead_name' | 'lead_company' | 'status';
  direction: 'asc' | 'desc';
}

// Notification interfaces
export interface LeadResponseNotification {
  id: string;
  type: 'new_lead_response' | 'reply_requested' | 'reply_sent';
  title: string;
  message: string;
  data?: {
    leadResponseId?: string;
    replyRequestId?: string;
    campaignId?: string;
  };
  read: boolean;
  created_at: string;
}

// Status badge configuration
export interface StatusBadgeConfig {
  [key in LeadResponseStatus]: {
    label: string;
    className: string;
    description: string;
  };
}

export const STATUS_BADGE_CONFIG: StatusBadgeConfig = {
  new: {
    label: 'New',
    className: 'bg-blue-100 text-blue-800',
    description: 'New lead response from CSR'
  },
  read: {
    label: 'Read',
    className: 'bg-gray-100 text-gray-800',
    description: 'Response has been read by user'
  },
  reply_requested: {
    label: 'Reply Requested',
    className: 'bg-yellow-100 text-yellow-800',
    description: 'User has requested a reply'
  },
  reply_sent: {
    label: 'Reply Sent',
    className: 'bg-green-100 text-green-800',
    description: 'CSR has sent the reply'
  },
  closed: {
    label: 'Closed',
    className: 'bg-red-100 text-red-800',
    description: 'Conversation has been closed'
  }
};

// LinkedIn URL validation
export const LINKEDIN_URL_REGEX = /^https?:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-]+\/?$/;

export const isValidLinkedInUrl = (url: string): boolean => {
  return LINKEDIN_URL_REGEX.test(url);
};

// Conversation parsing types
export interface ConversationParseResult {
  success: boolean;
  messages: ConversationMessage[];
  error?: string;
}

// Form validation types
export interface ValidationError {
  field: string;
  message: string;
}

export interface FormValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

// Role-based action permissions
export interface LeadResponseActions {
  canCreate: boolean;
  canRead: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  canRequestReply: boolean;
  canMarkReplySent: boolean;
  canClose: boolean;
}

export const getLeadResponseActions = (userRole: string): LeadResponseActions => {
  switch (userRole) {
    case 'csr':
    case 'admin':
    case 'super_admin':
      return {
        canCreate: true,
        canRead: true,
        canUpdate: true,
        canDelete: false,
        canRequestReply: false,
        canMarkReplySent: true,
        canClose: true
      };
    case 'user':
    case 'manager':
      return {
        canCreate: false,
        canRead: true,
        canUpdate: true, // Only status updates like marking as read
        canDelete: false,
        canRequestReply: true,
        canMarkReplySent: false,
        canClose: true
      };
    default:
      return {
        canCreate: false,
        canRead: false,
        canUpdate: false,
        canDelete: false,
        canRequestReply: false,
        canMarkReplySent: false,
        canClose: false
      };
  }
};